import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { getMaterialStateColor } from '../utils/colors';
import Icon from './Icon';
import { materialState } from '../utils/enums';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { capitalizeFirstLetter } from '../utils/capitalize';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';
import { Badge } from '@mui/material';

export interface MenuItemDraggable {
    label: string;
    shortcut: string;
    action: (() => void) | undefined;
}

interface DraggableCellProps {
    dragIds: { lineId: string; itemId: string };
    index: number;
    onDragStart?: () => void;
    itemType: string;
    children: React.ReactNode;
    showDelete?: boolean;
    onDelete?: () => void;
    disabled?: boolean;
    state?: number;
    onDuplicate?: () => void;
    onDoubleClick?: () => void;
    warning?: boolean;
    warningChar?: string;
    menuItems?: MenuItemDraggable[];
    disableMenu?: boolean;
}

const DraggableCell: React.FC<DraggableCellProps> = ({
    children,
    dragIds,
    index,
    onDragStart = () => {},
    itemType,
    showDelete = true,
    onDelete = () => {},
    disabled = false,
    state = materialState.PRESENT,
    onDuplicate = () => {},
    onDoubleClick,
    warning = false,
    warningChar = '!',
    menuItems = [],
    disableMenu = false,
}) => {
    const { t } = useTranslation();
    const { mode } = useTheme();
    const [{ isDragging }, dragRef, preview] = useDrag({
        type: itemType,
        item: { dragIds },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        canDrag: !disabled,
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (event.ctrlKey && event.key === 'b') {
            onDuplicate();
        }
        if (event.key === 'Delete') {
            onDelete();
        }
    };

    const [menuAnchor, setMenuAnchor] = useState<null | { mouseX: number; mouseY: number }>(null);

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) return;
        setMenuAnchor({ mouseX: event.clientX + 2, mouseY: event.clientY - 6 });
    };

    const handleClose = () => {
        setMenuAnchor(null);
    };

    const menuActions: MenuItemDraggable[] = [
        ...menuItems,
        {
            label: t('duplicate'),
            shortcut: 'Ctrl + B',
            action: onDuplicate,
        },
        {
            label: t('edit'),
            shortcut: t('doubleClick'),
            action: onDoubleClick,
        },
        {
            label: t('delete'),
            shortcut: capitalizeFirstLetter(t('del')),
            action: onDelete,
        },
    ];

    return (
        <Badge invisible={!warning} color="warning" badgeContent={warningChar} className="w-100">
            <div
                ref={dragRef}
                onContextMenu={handleContextMenu}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                aria-disabled={disabled}
                className={`fx-r max-w-500px bg-gray border-radius-10px fx-fg-1 min-h-25px ${warning && 'outline-1px'} ${!disabled && 'hover-black focus-outline'}`}
                style={{
                    backgroundColor: getMaterialStateColor(state),
                    opacity: isDragging ? 0.5 : 1,
                    cursor: disabled ? 'default' : 'pointer',
                    transform: 'translate3d(0, 0, 0)',
                    outlineColor: warning ? 'darkorange' : mode === 'dark' ? 'whitesmoke' : 'black',
                }}
                onDragStart={!disabled ? onDragStart : undefined}
            >
                <div
                    className="draggable-children"
                    style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', userSelect: 'text' }}
                    onDoubleClick={disabled ? undefined : onDoubleClick}
                >
                    {children}
                </div>

                <Menu
                    open={!disableMenu && Boolean(menuAnchor)}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={menuAnchor !== null ? { top: menuAnchor.mouseY, left: menuAnchor.mouseX } : undefined}
                >
                    {menuActions.map(({ label, shortcut, action }) => (
                        <MenuItem
                            key={label}
                            onClick={() => {
                                handleClose();
                                action?.();
                            }}
                            className="gap-20px"
                        >
                            {capitalizeFirstLetter(label)}
                            <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: '0.85em' }}>{shortcut}</span>
                        </MenuItem>
                    ))}
                </Menu>
            </div>
        </Badge>
    );
};

export default DraggableCell;
